import React, { useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react'
import { Box } from '@mui/material'
import { EmtDistance, EmtTypography } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'

import { ServiceSection } from '../pet-sitter/reservations/NewRequest/ServiceSection'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import {
  FormSlot,
  ServiceSlug,
  DateTimeInstance,
  getSlotType,
  makeBlankSlot,
} from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import { useDisplayDistance } from '@/hooks'

type Props = {
  isMobile: boolean
  selectedService: string | null
  formRefs: React.MutableRefObject<Record<string, any>>
  // ⚠️ in this flow it's { selectedService, formSlots } (wrapper), not actual form initial values
  localStorageInitialValues: any
  onServiceChange: (value: string) => void
  serviceSectionRef?: React.RefObject<HTMLDivElement>
  showDistanceRange?: boolean
  showAddDates?: boolean
}

const initFormSlots = (): Record<ServiceSlug, FormSlot[]> => ({
  promenade: [makeBlankSlot('promenade', null)],
  'garde-chez-vous': [makeBlankSlot('garde-chez-vous', null)],
  'garde-chez-eux': [makeBlankSlot('garde-chez-eux', null)],
  visite: [makeBlankSlot('visite', null)],
})

const ServiceContentSection = forwardRef<any, Props>(
  (
    {
      isMobile,
      selectedService,
      formRefs,
      localStorageInitialValues,
      onServiceChange,
      serviceSectionRef,
      showDistanceRange = true,
      showAddDates = true,
    },
    ref,
  ) => {
    const t = useTranslation('petsitter').t
    const displayDistance = useDisplayDistance()

    const distance = useSignUpCallRequestStore((s) => s.distance)
    // const setDistance = useSignUpCallRequestStore((s) => s.setDistance)
    const [distanceDraft, setDistanceDraft] = useState(distance)

    const [errors, setErrors] = useState<Record<string, string>>({})

    const currentSlug: ServiceSlug | null = useMemo(() => {
      if (!selectedService) return null
      const slug = getSlotType(selectedService)
      return slug ? (slug as ServiceSlug) : null
    }, [selectedService])

    const hasHydrated = useSignUpCallRequestStore((s) => s.hasHydrated)
    const storedService = useSignUpCallRequestStore((s) => s.service)
    // const setService = useSignUpCallRequestStore((s) => s.setService)

    const [formSlotsByService, setFormSlotsByService] = useState<Record<ServiceSlug, FormSlot[]>>(initFormSlots)

    // ✅ Restore from store once hydrated
    useEffect(() => {
      if (!hasHydrated) return
      if (!storedService?.selectedService) return
      const slug = storedService.selectedService as ServiceSlug
      const slots = (storedService.formSlots ?? []) as FormSlot[]
      if (slots.length) setFormSlotsByService((prev) => ({ ...prev, [slug]: slots }))
    }, [hasHydrated, storedService?.selectedService, storedService?.formSlots])

    // ✅ wrap service change so reset happens ONLY on user action
    const handleServiceChange = (value: string) => {
      onServiceChange(value)

      const slug = getSlotType(value) as ServiceSlug | null
      if (!slug) return

      const isRecurrent = slug === 'promenade' || slug === 'visite'
      setFormSlotsByService((prev) => {
        const nextSlots = isRecurrent
          ? [makeBlankSlot(slug, null)]
          : prev[slug]?.length
          ? prev[slug]
          : [makeBlankSlot(slug, null)]
        // persist in store
        // setService({ selectedService: slug, formSlots: nextSlots })
        return { ...prev, [slug]: nextSlots }
      })
    }

    // ✅ persist on save/add/remove
    const addNewSlot = () => {
      if (!currentSlug) return
      setFormSlotsByService((prev) => {
        const next = [...prev[currentSlug], makeBlankSlot(currentSlug, null)]
        // setService({ selectedService: currentSlug, formSlots: next })
        return { ...prev, [currentSlug]: next }
      })
    }

    const handleSaveSlot = (slotId: string, instance: DateTimeInstance) => {
      if (!currentSlug) return
      setFormSlotsByService((prev) => {
        const next = prev[currentSlug].map((s) => (s.id === slotId ? { ...s, data: instance } : s))
        // setService({ selectedService: currentSlug, formSlots: next })
        return { ...prev, [currentSlug]: next }
      })
    }

    const removeSlot = (slotId: string) => {
      if (!currentSlug) return
      setFormSlotsByService((prev) => {
        const filtered = prev[currentSlug].filter((s) => s.id !== slotId)
        const next = filtered.length ? filtered : [makeBlankSlot(currentSlug, null)]
        // setService({ selectedService: currentSlug, formSlots: next })
        return { ...prev, [currentSlug]: next }
      })
    }

    useImperativeHandle(ref, () => ({
      async validate() {
        if (!currentSlug) {
          setErrors((prev) => ({ ...prev, selectedService: 'Veuillez sélectionner un service.' }))
          return false
        }

        let allOK = true
        for (const slot of formSlotsByService[currentSlug] ?? []) {
          const slotRef = formRefs.current[slot.id]
          // DateTimeForm and DateTimeFormRecurrent must both expose validateOnly()
          if (slotRef?.validateOnly) {
            const ok = slotRef.validateOnly()
            if (!ok) allOK = false
          }
        }

        if (!allOK) return false
        setErrors({})
        return true
      },

      getValues() {
        return {
          selectedService: currentSlug,
          formSlots: currentSlug ? formSlotsByService[currentSlug] : [],
          distance: distanceDraft,
        }
      },
    }))

    // Distance mapping
    const distanceCorrespondingValues = {
      percentToValue: { 0: 0, 10: 1, 20: 2, 30: 3, 40: 4, 50: 5, 60: 10, 70: 15, 80: 20, 90: 30, 100: 50 },
      valueToPercent: { 0: 0, 1: 10, 2: 20, 3: 30, 4: 40, 5: 50, 10: 60, 15: 70, 20: 80, 30: 90, 50: 100 },
    }

    const [distancePercent, setDistancePercent] = useState<[number, number]>([
      distanceCorrespondingValues.valueToPercent[distance.min],
      distanceCorrespondingValues.valueToPercent[distance.max],
    ])

    // ✅ keep slider UI synced if store changes from outside
    useEffect(() => {
      setDistancePercent([
        distanceCorrespondingValues.valueToPercent[distance.min],
        distanceCorrespondingValues.valueToPercent[distance.max],
      ])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [distance.min, distance.max])

    const handleDistanceLiveChange = (newValue: number | number[]) => {
      if (!Array.isArray(newValue)) return
      setDistancePercent(newValue as [number, number])

      const [minP, maxP] = newValue as [number, number]
      setDistanceDraft({
        min: distanceCorrespondingValues.percentToValue[minP],
        max: distanceCorrespondingValues.percentToValue[maxP],
      })
    }

    const isIOS =
      typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

    return (
      <Box ref={serviceSectionRef}>
        <ServiceSection
          isMobile={isMobile}
          selectedService={selectedService}
          errors={errors}
          formSlotsByService={formSlotsByService}
          formRefs={formRefs}
          // ✅ important fix: don't pass wrapper object here
          localStorageInitialValues={null}
          onServiceChange={handleServiceChange}
          onAddNewSlot={addNewSlot}
          addButton={false}
          onSaveSlot={handleSaveSlot}
          onRemoveSlot={removeSlot}
          showSelect
          showPrices={true}
        />

        {errors.selectedService && (
          <EmtTypography data-form-error="true" variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
            {errors.selectedService}
          </EmtTypography>
        )}
        {errors.dateTimeInstances && (
          <EmtTypography data-form-error="true" variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
            {errors.dateTimeInstances}
          </EmtTypography>
        )}

        {selectedService === 'garde-chez-eux' && showDistanceRange && (
          <>
            <EmtTypography variant="body1" fontWeight="700" sx={{ marginBottom: '46px', marginTop: '30px' }}>
              {t('reservations.newRequests.labels.distanceRange')}
            </EmtTypography>

            <Box sx={{ width: 'calc(100% - 44px)', marginLeft: '22px' }}>
              <EmtDistance
                // @ts-expect-error
                color="purple"
                min={0}
                max={100}
                minDistanceBetweenThumbs={10}
                value={distancePercent}
                onChange={(value: number | number[], _event: Event) => {
                  if (isIOS && _event?.type === 'mousedown') return
                  handleDistanceLiveChange(value)
                }}
                marks={[
                  { value: 0, label: displayDistance(0) },
                  { value: 10 },
                  { value: 20 },
                  { value: 30 },
                  { value: 40 },
                  { value: 50 },
                  { value: 60 },
                  { value: 70 },
                  { value: 80 },
                  { value: 100, label: displayDistance(50) },
                ]}
                valueLabelFormat={(v: number) =>
                  displayDistance(
                    distanceCorrespondingValues.percentToValue[v] || distanceCorrespondingValues.valueToPercent[v],
                    '',
                    1,
                  )
                }
                step={10}
              />
            </Box>
          </>
        )}
      </Box>
    )
  },
)

ServiceContentSection.displayName = 'ServiceContentSection'
export default ServiceContentSection
